<template>
  <div class="lite_popup lite_app_manage" v-show="show" @click.stop>
    <div class="lite_popup_box lite_app_manage_box">
      <div class="lite_popup_title">
        <span>{{ $t('language.liteApp.title') }}</span>
        <div class="icon white-close dialog-close" @click="close"></div>
      </div>
      <div class="lite_popup_content myscroll">
        <div v-if="!editing" class="lite_app_list">
          <div class="lite_app_toolbar">
            <span>{{ $t('language.liteApp.description') }}</span>
            <button @click="startCreate">{{ $t('language.liteApp.addApp') }}</button>
          </div>
          <div v-if="loading" class="lite_app_loading">{{ $t('language.common.load') }}</div>
          <div v-else-if="apps.length === 0" class="lite_app_empty">{{ $t('language.liteApp.empty') }}</div>
          <div v-else class="lite_app_item" v-for="app in apps" :key="app.id">
            <div class="lite_app_item_name">
              <i class="lite_app_drive_icon" :class="'drive-icon-' + app.type"></i>
              <span>{{ cloudName(app.type) }} · {{ app.name }}</span>
            </div>
            <div class="lite_app_item_meta">{{ app.key }}<span v-if="app.inUse"> · {{ $t('language.liteApp.inUse') }}</span></div>
            <div class="lite_app_actions">
              <button @click="startEdit(app)">{{ $t('language.liteApp.edit') }}</button>
              <button :disabled="app.inUse" @click="remove(app)">{{ $t('language.common.delete') }}</button>
            </div>
          </div>
        </div>
        <el-form v-else :model="draft" label-width="200px" class="lite_app_form">
          <el-form-item :label="$t('language.liteApp.appType')">
            <div class="lite_app_type_select">
              <i class="lite_app_drive_icon" :class="'drive-icon-' + draft.type"></i>
              <el-select v-model="draft.type" :disabled="!!draft.id" class="lite_app_select">
                <el-option v-for="type in appTypes" :key="type" :label="cloudName(type)" :value="type">
                  <span class="lite_app_option">
                    <i class="lite_app_drive_icon" :class="'drive-icon-' + type"></i>
                    <span>{{ cloudName(type) }}</span>
                  </span>
                </el-option>
              </el-select>
            </div>
          </el-form-item>
          <el-form-item :label="$t('language.liteApp.appName')">
            <el-input v-model="draft.name" maxlength="255"></el-input>
          </el-form-item>
          <el-form-item :label="$t('language.liteApp.clientId')">
            <el-input v-model.trim="draft.key" :disabled="draft.inUse"></el-input>
          </el-form-item>
          <el-form-item :label="$t('language.liteApp.clientSecret')">
            <el-input v-model.trim="draft.secret" :type="showSecret ? 'text' : 'password'" autocomplete="new-password" :placeholder="draft.id ? $t('language.liteApp.secretUnchanged') : ''" :disabled="draft.inUse">
              <template #suffix>
                <div :class="showSecret ? 'open_eye' : 'close_eye'" @click="showSecret = !showSecret"></div>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item v-if="draft.callback" :label="$t('language.liteApp.callback')">
            <el-input class="lite_app_callback_input" :value="draft.callback" readonly>
              <i slot="suffix" class="copy lite_app_copy_icon" :title="$t('language.common.copy')" @click.stop="copyCallback"></i>
            </el-input>
          </el-form-item>
          <div v-if="draft.inUse" class="lite_app_used_tip">{{ $t('language.liteApp.usedTip') }}</div>
        </el-form>
      </div>
      <div class="lite_popup_footer" v-if="editing">
        <button class="lite_app_cancel" :disabled="saving" @click="cancelEdit">{{ $t('language.common.cancel') }}</button>
        <button :disabled="saving" @click="save">{{ $t('language.common.save') }}</button>
      </div>
    </div>
    <liteConfirm
      ref="deleteConfirm"
      :showCancel="true"
      :autoClose="false"
      :okText="$t('language.common.delete')"
      :data="deleteConfirmData"
      @ok="confirmRemove"
    />
  </div>
</template>

<script>
import { Message } from 'element-ui';
import requestUtils from '@/js/request_util.js';
import liteConfirm from '@/components/workspace/lite_confirm.vue';

export default {
  components: { liteConfirm },
  data() {
    return {
      show: false,
      loading: false,
      saving: false,
      editing: false,
      showSecret: false,
      apps: [],
      deleteAppId: '',
      deleteConfirmData: { title: '', desc: '' },
      originalKey: '',
      draft: this.emptyDraft(),
      appTypes: [
        'GoogleWorkspace', 'GoogleDrive', 'OneDrive', 'Dropbox', 'PCloud', 'Box',
        'PutIO', 'FourShared', 'MediaFire', 'GooglePhotos', 'OneDriveBusiness',
        'DropboxBusiness', 'BoxBusiness', 'SharePoint', 'BaiDu', 'Flickr', 'Evernote',
        'SugarSync', 'Gmail', 'Outlook'
      ],
    };
  },
  methods: {
    emptyDraft() {
      return { id: '', type: 'GoogleDrive', name: '', key: '', secret: '', callback: '', inUse: false };
    },
    open() {
      this.show = true;
      this.editing = false;
      this.load();
    },
    close() {
      if (!this.saving) {
        this.show = false;
        this.editing = false;
      }
    },
    load() {
      this.loading = true;
      requestUtils.doLiteRequest('/app/config/list', {}).then(res => {
        this.loading = false;
        if (res.code === 200) {
          this.apps = res.data || [];
        } else {
          Message.error(this.errorMessage(res.msg || res.message));
        }
      }, err => {
        this.loading = false;
        Message.error(err);
      });
    },
    startCreate() {
      this.originalKey = '';
      this.showSecret = false;
      this.draft = this.emptyDraft();
      this.editing = true;
      this.loadCallback(this.draft.type);
    },
    startEdit(app) {
      this.originalKey = app.key || '';
      this.showSecret = false;
      this.draft = {
        id: app.id,
        type: app.type,
        name: app.name,
        key: app.key || '',
        secret: '',
        callback: this.currentCallback(),
        inUse: !!app.inUse,
      };
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
      this.showSecret = false;
      this.draft = this.emptyDraft();
    },
    loadCallback(type) {
      this.draft.callback = this.currentCallback();
      requestUtils.doLiteRequest('/app/config/available', { type: type }).then(res => {
        if (res.code === 200 && this.editing && !this.draft.id && this.draft.type === type) {
          this.draft.callback = (res.data || {}).callback || this.currentCallback();
        }
      });
    },
    currentCallback() {
      switch (location.hostname) {
        case 'test.multcloud.com':
          return 'https://api.multcloud.com/drives/callback';
        case 'app.multcloud.com':
          return 'https://api2.multcloud.com/drives/callback';
        default:
          return 'https://localhost/drives/callback';
      }
    },
    copyCallback() {
      const callback = this.draft.callback;
      if (!callback) return;
      const copied = () => Message.success(this.$t('language.liteApp.copySuccess'));
      const fallback = () => {
        const textarea = document.createElement('textarea');
        textarea.value = callback;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          copied();
        } catch (e) {
          Message.error(this.$t('language.common.failure'));
        } finally {
          document.body.removeChild(textarea);
        }
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(callback).then(copied, fallback);
      } else {
        fallback();
      }
    },
    save() {
      if (!this.draft.name || !this.draft.name.trim()) {
        Message.warning(this.$t('language.liteApp.nameRequired'));
        return;
      }
      const creating = !this.draft.id;
      const payload = {
        id: this.draft.id,
        type: this.draft.type,
        name: this.draft.name,
        key: this.draft.key,
        secret: this.draft.secret,
      };
      if (creating && (!payload.key || !payload.secret)) {
        Message.warning(this.$t('language.liteApp.credentialsRequired'));
        return;
      }
      if (!creating && !payload.secret) {
        if (payload.key !== this.originalKey) {
          Message.warning(this.$t('language.liteApp.secretRequiredForKey'));
          return;
        }
        // 空密钥表示只改名称，后端会保留既有凭据。
        payload.key = '';
      }
      this.saving = true;
      requestUtils.doLiteRequest(creating ? '/app/config/create' : '/app/config/update', payload).then(res => {
        this.saving = false;
        if (res.code === 200) {
          this.editing = false;
          this.draft = this.emptyDraft();
          this.load();
        } else {
          Message.error(this.errorMessage(res.msg || res.message));
        }
      }, err => {
        this.saving = false;
        Message.error(err);
      });
    },
    remove(app) {
      if (app.inUse) return;
      this.deleteAppId = app.id;
      this.deleteConfirmData.title = this.$t('language.liteApp.warning');
      this.deleteConfirmData.desc = this.$t('language.liteApp.deleteConfirm');
      this.$refs.deleteConfirm.show = true;
    },
    confirmRemove() {
      const confirm = this.$refs.deleteConfirm;
      if (!this.deleteAppId || confirm.loading) return;
      confirm.loading = true;
      requestUtils.doLiteRequest('/app/config/delete', { id: this.deleteAppId }).then(res => {
        confirm.loading = false;
        if (res.code === 200) {
          confirm.show = false;
          this.deleteAppId = '';
          this.load();
        } else {
          Message.error(this.errorMessage(res.msg || res.message));
        }
      }, err => {
        confirm.loading = false;
        Message.error(err);
      });
    },
    errorMessage(message) {
      const messages = {
        app_in_use: this.$t('language.liteApp.appInUse'),
        app_name_exists: this.$t('language.liteApp.nameExists'),
        app_key_secret_required: this.$t('language.liteApp.credentialsRequired'),
        system_no_default_app: this.$t('language.liteApp.systemAppUnavailable'),
      };
      return messages[message] || message || this.$t('language.common.failure');
    },
    cloudName(type) {
      const names = {
        GoogleWorkspace: 'Google Workspace', GoogleDrive: 'Google Drive', OneDrive: 'OneDrive',
        Dropbox: 'Dropbox', PCloud: 'pCloud', Box: 'Box', PutIO: 'Put.io',
        FourShared: '4shared', MediaFire: 'MediaFire', GooglePhotos: 'Google Photos',
        OneDriveBusiness: 'OneDrive for Business', DropboxBusiness: 'Dropbox Business',
        BoxBusiness: 'Box for Business', SharePoint: 'SharePoint Online', BaiDu: 'Baidu',
        Flickr: 'Flickr', Evernote: 'Evernote', SugarSync: 'SugarSync', Gmail: 'Gmail', Outlook: 'Outlook',
      };
      return names[type] || type;
    },
  },
  watch: {
    'draft.type'(type) {
      if (this.editing && !this.draft.id) {
        this.loadCallback(type);
      }
    },
  },
};
</script>

<style scoped>
.lite_app_manage_box { width: 680px; }
.lite_app_toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; color: #6f7886; }
.lite_app_toolbar button, .lite_app_actions button { border: 0; background: none; color: #5267bf; cursor: pointer; }
.lite_app_toolbar button { padding: 7px 14px; color: #fff; background: #5267bf; border-radius: 3px; }
.lite_app_item { min-height: 58px; padding: 12px 8px; border-top: 1px solid #edf0f6; position: relative; }
.lite_app_item_name { color: #333; font-size: 14px; padding-right: 120px; display: flex; align-items: center; }
.lite_app_item_meta { margin-top: 7px; color: #9aa3b4; font-size: 12px; }
.lite_app_actions { position: absolute; right: 8px; top: 20px; }
.lite_app_actions button:disabled { color: #c9cfd9; cursor: not-allowed; }
.lite_app_empty, .lite_app_loading { padding: 45px 0; color: #9aa3b4; text-align: center; }
.lite_app_select { width: 100%; }
.lite_app_type_select { position: relative; }
.lite_app_type_select>.lite_app_drive_icon {
  position: absolute;
  z-index: 1;
  top: 20px;
  left: 12px;
  pointer-events: none;
}
.lite_app_type_select >>> .el-input__inner { padding-left: 42px; }
.lite_app_drive_icon { display: inline-block; width: 18px; height: 18px; flex: 0 0 18px; margin-right: 8px; background-position: center; background-repeat: no-repeat; background-size: contain; }
.lite_app_option { display: flex; align-items: center; height: 34px; }
.lite_app_option .lite_app_drive_icon { width: 18px; height: 18px; }
.lite_app_callback_input >>> .el-input__inner { padding-right: 38px; }
.lite_app_copy_icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-top: 21px;
  margin-right: 10px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  cursor: pointer;
  opacity: .75;
}
.lite_app_copy_icon:hover { opacity: 1; }
.lite_app_form >>> .el-form-item { display: flex; align-items: center; }
.lite_app_form >>> .el-form-item__label {
  width: 200px !important;
  height: 44px;
  line-height: 44px;
  padding: 0 14px 0 0;
  box-sizing: border-box;
  text-align: right;
  position: relative;
  top: 5px;
}
.lite_app_form >>> .el-form-item__content {
  flex: 1;
  margin-left: 0 !important;
  line-height: normal;
}
.lite_app_used_tip { margin: 0 0 12px 128px; color: #e6a23c; font-size: 12px; }
.lite_app_cancel { margin-right: 10px; background: #fff !important; color: #5267bf !important; border: 1px solid #5267bf !important; }
.lite_app_form >>> .el-input__icon {
  height: 55px;
}
</style>



// WEBPACK FOOTER //
// src/components/popup/personal_center_popups/lite_app_manage_popup.vue